import React, { useState } from 'react';
import { Card, CardTitle, Metric, Button } from '../components/UI';
import { equipmentOptions, runOptimization, createShipment } from '../api';
import RouteMap from '../components/RouteMap';
import StopsEditor from '../components/StopsEditor';
import HosPanel from '../components/HosPanel';
import { Loader2, Share2 } from 'lucide-react';

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-xs block mb-1.5" style={{ color: 'var(--lf-muted)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2.5 rounded-lg text-sm bg-white outline-none';
const inputStyle = { border: '1px solid #d8dde6' };

export default function Optimize() {
  const [form, setForm] = useState({
    origin: 'Charlotte, NC',
    destination: 'Raleigh, NC',
    stops: [],
    weight: 5000,
    equipment: "53' Dry Van",
    pickup: '2026-10-07T08:00',
    delivery: '2026-10-07T16:00',
    constraints: 'Avoid tolls if delay is under 30 minutes; prioritize lowest total cost',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const payload = () => ({
    origin: form.origin,
    destination: form.destination,
    stops: form.stops,
    weight: Number(form.weight),
    equipment: form.equipment,
    pickup: form.pickup,
    delivery: form.delivery,
    constraints: form.constraints,
  });

  const handleRun = async () => {
    setLoading(true);
    setError('');
    setSavedMsg('');
    setShareCopied(false);
    try {
      const r = await runOptimization(payload());
      setResult(r);
    } catch (e) {
      setError('Failed to run optimizer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setResult(null);
    setSavedMsg('');
    setError('');
    setShareCopied(false);
  };

  const handleAccept = async () => {
    setSaving(true);
    setError('');
    try {
      const ship = await createShipment(payload());
      await runOptimization({ ...payload(), shipment_id: ship.id });
      setSavedMsg(`Shipment ${ship.shipment_code} created and optimized.`);
    } catch (e) {
      setError('Could not save shipment.');
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (!result) return;
    const url = `${window.location.origin}/share/plan/${result.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (e) {
      window.prompt('Copy this link:', url);
    }
  };

  return (
    <div className="space-y-[18px]">
      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-[18px]">
        <Card>
          <CardTitle>New shipment</CardTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Origin">
              <input className={inputCls} style={inputStyle} value={form.origin} onChange={(e) => update('origin', e.target.value)} />
            </Field>
            <Field label="Destination">
              <input className={inputCls} style={inputStyle} value={form.destination} onChange={(e) => update('destination', e.target.value)} />
            </Field>
            <div className="sm:col-span-2">
              <StopsEditor stops={form.stops} onChange={(v) => update('stops', v)} />
            </div>
            <Field label="Weight (lb)">
              <input className={inputCls} style={inputStyle} type="number" value={form.weight} onChange={(e) => update('weight', e.target.value)} />
            </Field>
            <Field label="Equipment">
              <select className={inputCls} style={inputStyle} value={form.equipment} onChange={(e) => update('equipment', e.target.value)}>
                {equipmentOptions.map((eq) => (
                  <option key={eq}>{eq}</option>
                ))}
              </select>
            </Field>
            <Field label="Pickup">
              <input className={inputCls} style={inputStyle} type="datetime-local" value={form.pickup} onChange={(e) => update('pickup', e.target.value)} />
            </Field>
            <Field label="Delivery deadline">
              <input className={inputCls} style={inputStyle} type="datetime-local" value={form.delivery} onChange={(e) => update('delivery', e.target.value)} />
            </Field>
            <Field label="Constraints" full>
              <input className={inputCls} style={inputStyle} value={form.constraints} onChange={(e) => update('constraints', e.target.value)} />
            </Field>
          </div>
          <div className="flex gap-2.5 mt-4 items-center flex-wrap">
            <Button variant="primary" onClick={handleRun} disabled={loading}>
              {loading ? (<><Loader2 className="animate-spin" size={15} /> Running…</>) : 'Run Lindore AI'}
            </Button>
            <Button variant="secondary" onClick={handleClear}>Clear</Button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        </Card>

        <Card>
          <CardTitle>Optimization result</CardTitle>
          {!result ? (
            <p className="text-sm" style={{ color: 'var(--lf-muted)' }}>
              Enter shipment details and run the AI optimizer.
            </p>
          ) : (
            <div className="lf-fade-in">
              <div className="flex justify-between items-center mb-2">
                <b className="text-sm">Recommended plan</b>
                <b className="text-sm" style={{ color: 'var(--lf-green)' }}>{result.score} / 100</b>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: '#e9edf4' }}>
                <div
                  className="lf-score-bar block h-full"
                  style={{ background: 'var(--lf-green)', width: `${result.score}%` }}
                />
              </div>
              <div
                className="mt-3.5 py-3 px-3.5 rounded-lg"
                style={{ background: '#f8faff', borderLeft: '4px solid var(--lf-blue)' }}
              >
                <b className="text-[15px]">
                  {[result.origin, ...(result.stops || []), result.destination].join(' → ')}
                </b>
                <small className="block mt-1.5" style={{ color: 'var(--lf-muted)' }}>
                  Primary route • estimated {result.duration} • {result.miles} miles
                  {result.stops && result.stops.length > 0 ? ` • ${result.stops.length} stop${result.stops.length > 1 ? 's' : ''}` : ''}
                  &nbsp;• delivery window met
                </small>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Metric label="Estimated cost" value={`$${result.cost.toLocaleString()}`} />
                <Metric label="Fuel" value={`$${result.fuel.toLocaleString()}`} />
                <Metric label="ETA" value={result.eta} />
                <Metric label="Weight" value={`${Number(result.weight).toLocaleString()} lb`} />
              </div>
              <p className="leading-relaxed mt-3.5 text-sm" style={{ color: '#475467' }}>
                <b>Why:</b> {result.explanation}
              </p>
              <HosPanel hos={result} />
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <Button variant="primary" onClick={handleAccept} disabled={saving}>
                  {saving ? (<><Loader2 className="animate-spin" size={15} /> Saving…</>) : 'Accept plan'}
                </Button>
                <Button variant="secondary" onClick={handleShare}>
                  <Share2 size={14} /> {shareCopied ? 'Link copied!' : 'Share plan'}
                </Button>
                {savedMsg && (
                  <span className="text-xs font-semibold" style={{ color: 'var(--lf-green)' }}>
                    {savedMsg}
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card>
        <CardTitle>Route preview</CardTitle>
        <RouteMap
          origin={form.origin}
          destination={form.destination}
          stops={form.stops}
          height={340}
        />
      </Card>
    </div>
  );
}
